import styled from '@emotion/styled'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  address?: string
  image?: ImageProps
  googleMapsURL?: string
  languageCode: string
  siteName?: string
}

export const Map = memo(function Map({
  address,
  image,
  googleMapsURL,
  languageCode,
  siteName = 'Hotel Saturnia & International',
}: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  })

  return (
    <Container ref={animObjectRef}>
      {image ? (
        <FadeInUp>
          <Banner className="item-banner">
            <Image {...image} />
          </Banner>
        </FadeInUp>
      ) : null}

      <Wrapper>
        {siteName ? (
          <FadeInUp>
            <Title>{siteName}</Title>
          </FadeInUp>
        ) : null}

        {address ? (
          <FadeInUp delay={1}>
            <Address dangerouslySetInnerHTML={{ __html: address }} />
          </FadeInUp>
        ) : null}

        {googleMapsURL ? (
          <FadeInUp delay={1.2}>
            <CTA
              label={useVocabularyData('open-google-maps', languageCode)}
              rel="noopener"
              target="_blank"
              URL={googleMapsURL}
              variant="invert"
            />
          </FadeInUp>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  margin: 0 auto;

  @media (max-width: 1199px) {
    margin: 3rem auto 0;
    padding: 0 1.5rem;
  }
`

const Banner = styled.div`
  margin: 3.75rem 12.153vw 0;
  aspect-ratio: 1.94;
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    margin: 0;
    aspect-ratio: 1.2;
  }
`

const Wrapper = styled.div`
  margin: 0 auto;
  padding: 3rem;
  text-align: center;
  max-width: 30.625rem;
  background-color: ${({ theme }) => theme.colors.variants.primaryLight};
  position: relative;
  top: -3.75rem;

  @media (max-width: 1199px) {
    width: calc(100% - 3rem);
    padding: 2.25rem 3rem;
  }
`

const Title = styled.h3`
  ${styles.title.small}

  text-transform: none;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  margin-bottom: 0.75rem;
`

const Address = styled.div`
  ${styles.description.dark}

  text-transform: none;
`

const CTA = styled(Button)`
  margin-top: 2.25rem;
  text-transform: none;
  display: inline-block;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
  }
`
